import React from 'react';
import { createTheme } from '@mui/material/styles';
import { colors, gradients, fonts, numeric, radius } from './theme/tokens';
// FightDecks icons for MUI internal glyph slots (Select arrow, Checkbox, Alert
// severities, Chip delete, Autocomplete). Themed here so built-in icons match.
import {
  ChevronDownIcon, CloseIcon, CheckboxBlankIcon, CheckboxCheckedIcon,
  SuccessIcon, InfoIcon, WarningIcon, ErrorIcon,
} from './components/icons';

// Collector's Vault theme — built entirely from src/theme/tokens.js.
// Depth comes from background steps + 1px steel borders; shadows are disabled
// globally, edges are hard, brass is reserved for the things that matter.
// Octagon silhouette — UFC-cage corners on contained buttons. The cut scales
// with button size. (clip-path clips borders too, so outlined buttons keep
// their rectangles; the octagon is the contained CTA's signature.)
const octagon = (cut) => ({
  borderRadius: 0,
  clipPath: `polygon(${cut}px 0, calc(100% - ${cut}px) 0, 100% ${cut}px, 100% calc(100% - ${cut}px), calc(100% - ${cut}px) 100%, ${cut}px 100%, 0 calc(100% - ${cut}px), 0 ${cut}px)`,
});

const theme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: colors.brass,
      light: colors.brassBright,
      dark: colors.brassDark,
      contrastText: colors.void,
    },
    secondary: {
      main: colors.steelBlue,
      contrastText: colors.textPrimary,
    },
    success: {
      main: colors.green,
      contrastText: colors.void,
    },
    info: {
      main: colors.steelBlue,
      contrastText: colors.textPrimary,
    },
    warning: {
      main: colors.coral,
      contrastText: colors.void,
    },
    error: {
      main: colors.redFg || colors.red,
      contrastText: colors.textPrimary,
    },
    background: {
      default: colors.void,
      paper: colors.panel,
      raised: colors.panelRaised,
    },
    text: {
      primary: colors.textPrimary,
      secondary: colors.textSecondary,
      disabled: 'rgba(138, 144, 153, 0.5)',
    },
    divider: colors.border,
    action: {
      active: colors.brass,
      hover: 'rgba(237, 234, 227, 0.04)',
      selected: 'rgba(196, 154, 92, 0.12)',
      disabled: 'rgba(138, 144, 153, 0.4)',
      disabledBackground: 'rgba(138, 144, 153, 0.12)',
    },
  },
  shape: {
    borderRadius: radius,
  },
  spacing: 8,
  shadows: Array(25).fill('none'),
  typography: {
    fontFamily: fonts.body,
    // Shapiro 85 is a single-weight super-heavy-wide face: weight stays 400
    // (faux-bold distorts it) and tracking stays near zero (it's already wide).
    h1: {
      fontFamily: fonts.display,
      fontWeight: 400,
      fontSize: '3.75rem',
      textTransform: 'uppercase',
      letterSpacing: '0.01em',
      lineHeight: 1.1,
    },
    h2: {
      fontFamily: fonts.display,
      fontWeight: 400,
      fontSize: '3rem',
      textTransform: 'uppercase',
      letterSpacing: '0.01em',
      lineHeight: 1.15,
    },
    h3: {
      fontFamily: fonts.display,
      fontWeight: 400,
      fontSize: '2.5rem',
      textTransform: 'uppercase',
      letterSpacing: '0.01em',
      lineHeight: 1.15,
    },
    h4: {
      fontFamily: fonts.display,
      fontWeight: 400,
      fontSize: '2rem',
      textTransform: 'uppercase',
      letterSpacing: '0.01em',
      lineHeight: 1.2,
    },
    h5: {
      fontFamily: fonts.display,
      fontWeight: 400,
      fontSize: '1.625rem',
      textTransform: 'uppercase',
      letterSpacing: '0.02em',
      lineHeight: 1.2,
    },
    h6: {
      fontFamily: fonts.display,
      fontWeight: 400,
      fontSize: '1.375rem',
      textTransform: 'uppercase',
      letterSpacing: '0.02em',
      lineHeight: 1.25,
    },
    subtitle1: {
      fontFamily: fonts.body,
      fontWeight: 600,
      fontSize: '1.125rem',
      lineHeight: 1.4,
    },
    subtitle2: {
      fontFamily: fonts.body,
      fontWeight: 600,
      fontSize: '0.9375rem',
      textTransform: 'uppercase',
      letterSpacing: '0.08em',
      color: colors.textSecondary,
      lineHeight: 1.4,
    },
    body1: {
      fontFamily: fonts.body,
      fontWeight: 500,
      fontSize: '1.125rem',
      lineHeight: 1.6,
    },
    body2: {
      fontFamily: fonts.body,
      fontWeight: 500,
      fontSize: '1rem',
      lineHeight: 1.6,
    },
    button: {
      fontFamily: fonts.display,
      fontWeight: 400,
      fontSize: '1.0625rem',
      textTransform: 'uppercase',
      letterSpacing: '0.02em',
    },
    caption: {
      fontFamily: fonts.body,
      fontWeight: 400,
      fontSize: '0.875rem',
      lineHeight: 1.5,
    },
    overline: {
      fontFamily: fonts.body,
      fontWeight: 600,
      fontSize: '0.8125rem',
      textTransform: 'uppercase',
      letterSpacing: '0.12em',
      lineHeight: 1.5,
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        html: {
          backgroundColor: colors.void,
        },
        body: {
          backgroundColor: colors.void,
          color: colors.textPrimary,
          fontFamily: fonts.body,
          fontFeatureSettings: numeric.fontFeatureSettings,
        },
        // Prose / content links: persistent underline + brass that clears contrast
        a: {
          color: colors.brassBright,
          textDecoration: 'underline',
          textUnderlineOffset: '0.15em',
          '&:hover, &:focus': {
            color: colors.brass,
            textDecoration: 'underline',
            outline: 0,
          },
        },
        input: {
          '&:-webkit-autofill, &:-webkit-autofill:hover, &:-webkit-autofill:focus, &:-webkit-autofill:active': {
            WebkitBoxShadow: `0 0 0 1000px ${colors.panel} inset`,
            WebkitTextFillColor: `${colors.textPrimary} !important`,
            caretColor: colors.textPrimary,
            transition: 'background-color 99999s ease-out 0s',
          },
        },
        '*::-webkit-scrollbar': {
          width: '10px',
          height: '10px',
        },
        '*::-webkit-scrollbar-track': {
          background: colors.void,
        },
        '*::-webkit-scrollbar-thumb': {
          background: colors.border,
          border: `2px solid ${colors.void}`,
        },
        '*::-webkit-scrollbar-thumb:hover': {
          background: colors.brass,
        },
        '::selection': {
          backgroundColor: 'rgba(212, 175, 55, 0.35)',
          color: colors.textPrimary,
        },
        ':focus-visible': {
          outline: `2px solid ${colors.brass}`,
          outlineOffset: '2px',
        },
        // Filmic grain — kills gradient banding on dark surfaces
        'body::after': {
          content: '""',
          position: 'fixed',
          inset: 0,
          zIndex: 9999,
          pointerEvents: 'none',
          backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='128' height='128'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.8' numOctaves='2' stitchTiles='stitch'/%3E%3CfeColorMatrix type='saturate' values='0'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.04'/%3E%3C/svg%3E")`,
        },
        '@media (prefers-reduced-motion: reduce)': {
          html: {
            scrollBehavior: 'auto',
          },
          '*, *::before, *::after': {
            animationDuration: '0.01ms !important',
            animationIterationCount: '1 !important',
            transitionDuration: '0.01ms !important',
            scrollBehavior: 'auto !important',
          },
        },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          color: colors.brassBright,
          textDecoration: 'underline',
          textUnderlineOffset: '0.15em',
          '&:hover': {
            color: colors.brass,
            textDecoration: 'underline',
          },
        },
      },
    },
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          borderRadius: radius,
          fontFamily: fonts.display,
          fontWeight: 400,
          textTransform: 'uppercase',
          letterSpacing: '0.02em',
          boxShadow: 'none',
          '&:hover': {
            boxShadow: 'none',
          },
        },
        // Hero CTAs lean forward: size="large" switches to the inclined cut
        sizeLarge: {
          fontFamily: fonts.impact,
          letterSpacing: '0.03em',
        },
        contained: {
          ...octagon(10),
          '&.MuiButton-sizeSmall': octagon(6),
          '&.MuiButton-sizeLarge': octagon(12),
        },
        containedPrimary: {
          backgroundColor: colors.brass,
          color: colors.void,
          '&:hover': {
            backgroundColor: colors.brassBright,
          },
          '&:active': {
            backgroundColor: colors.brassDark,
            transform: 'translateY(1px)',
          },
        },
        outlined: {
          borderColor: colors.border,
          color: colors.textPrimary,
          '&:hover': {
            borderColor: colors.textSecondary,
            backgroundColor: colors.panelRaised,
          },
        },
        text: {
          '&:hover': {
            backgroundColor: colors.panelRaised,
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          backgroundColor: colors.panel,
          border: `1px solid ${colors.border}`,
          borderRadius: radius,
          boxShadow: 'none',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          backgroundColor: colors.panel,
          border: `1px solid ${colors.border}`,
          borderRadius: radius,
          boxShadow: 'none',
          transition: 'background-color 150ms ease',
          '&:hover': {
            backgroundColor: colors.panelRaised,
          },
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          height: 6,
          borderRadius: 0,
          backgroundColor: colors.border,
        },
        bar: {
          borderRadius: 0,
        },
        barColorPrimary: {
          backgroundColor: colors.brass,
          backgroundImage: gradients.brassHorizontal,
        },
      },
    },
    MuiSelect: {
      defaultProps: {
        IconComponent: ChevronDownIcon,
      },
    },
    MuiCheckbox: {
      defaultProps: {
        icon: <CheckboxBlankIcon />,
        checkedIcon: <CheckboxCheckedIcon />,
      },
    },
    MuiAlert: {
      defaultProps: {
        iconMapping: {
          success: <SuccessIcon fontSize="inherit" />,
          info: <InfoIcon fontSize="inherit" />,
          warning: <WarningIcon fontSize="inherit" />,
          error: <ErrorIcon fontSize="inherit" />,
        },
      },
    },
    MuiAutocomplete: {
      defaultProps: {
        popupIcon: <ChevronDownIcon />,
        clearIcon: <CloseIcon />,
      },
    },
    MuiChip: {
      defaultProps: {
        size: 'small',
        deleteIcon: <CloseIcon />,
      },
      styleOverrides: {
        root: {
          borderRadius: radius,
          fontFamily: fonts.body,
          fontWeight: 600,
          fontSize: '0.8125rem',
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: `1px solid ${colors.border}`,
          fontFamily: fonts.body,
          fontFeatureSettings: numeric.fontFeatureSettings,
        },
        head: {
          color: colors.textSecondary,
          fontWeight: 600,
          fontSize: '0.85rem',
          textTransform: 'uppercase',
          letterSpacing: '0.08em',
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: colors.panelRaised,
          },
          '&.MuiTableRow-head:hover': {
            backgroundColor: 'transparent',
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          backgroundColor: colors.panel,
          borderBottom: `1px solid ${colors.border}`,
          boxShadow: 'none',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundImage: 'none',
          backgroundColor: colors.panel,
          borderRight: `1px solid ${colors.border}`,
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: colors.border,
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: radius,
          '&:hover': {
            backgroundColor: colors.panelRaised,
          },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: {
          backgroundColor: colors.brass,
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          fontFamily: fonts.body,
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          color: colors.textSecondary,
          '&.Mui-selected': {
            color: colors.brass,
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: radius,
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: colors.borderInput || colors.border,
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: colors.textSecondary,
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: colors.brass,
          },
        },
        input: {
          '&:-webkit-autofill, &:-webkit-autofill:hover, &:-webkit-autofill:focus, &:-webkit-autofill:active': {
            WebkitBoxShadow: `0 0 0 1000px ${colors.panel} inset`,
            WebkitTextFillColor: `${colors.textPrimary} !important`,
            caretColor: colors.textPrimary,
            transition: 'background-color 99999s ease-out 0s',
          },
        },
      },
    },
    MuiInput: {
      styleOverrides: {
        underline: {
          '&:before': {
            borderBottomColor: colors.borderInput || colors.border,
          },
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: colors.panelRaised,
          border: `1px solid ${colors.border}`,
          borderRadius: radius,
          color: colors.textPrimary,
          fontFamily: fonts.body,
        },
      },
    },
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 960,
      lg: 1280,
      xl: 1920,
    },
  },
});

export default theme;
